import './LivePublisher.sass';

import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { LiveHeader } from '$core/Live/LiveHeader/LiveHeader';
import { useDispatch, useSelector } from 'react-redux';
import { liveActions, liveSelector } from '$store/live';
import { useModalClose, useTranslator, useUserInfo } from '$hooks';
import { authSelector } from '$store/auth';
import { BottomBar, Button, Placeholder, RoundButton, Tabs, TabsItem } from '$uikit';
import { LiveMediaStream } from '$core/Live/LiveMediaStream/LiveMediaStream';
import { IcCamOff28, IcMic28, IcMicOff28, IcShare24, IcSwitch28 } from '$assets';
import { LiveHistoryFilterEnum, LiveStatus } from '$store/live/interface';
import { hideModal } from '$navigation/helper';
import { Loader, ShareSheet } from '$shared/components';
import { LiveTypes } from '$core/Live/LiveTypes/LiveTypes';
import { LiveHistory } from '$core/Live/LiveHistory/LiveHistory';
import { CameraTypeEnum } from '$shared/constants';
import { classNames as cn, statReachGoal } from '$utils';
import { AttachLink, StreamMessageModel } from '$store/models';
import { mainActions } from '$store/main';
import { LivePrompt } from '$core/Live/LivePrompt/LivePrompt';
import { LiveEndStatistics } from '../LiveEndStatistics/LiveEndStatistics';

export const LivePublisher: FC = () => {
  const dispatch = useDispatch();
  const {
    stream,
    cameras,
    reconnecting,
    togglingCamera,
    liveStatus,
    confirmStop,
    online,
    permission,
    micEnabled,
    cameraType,
    tipsMessagesCount,
    historyFilter,
    tipsTotal,
    focus,
  } = useSelector(liveSelector);
  const { currentUserId } = useSelector(authSelector);
  const user = useUserInfo(stream?.streamerId || currentUserId);
  const [hideOnlineStatus, setHideOnlineStatus] = useState(false);
  const displayCamSwitch = cameras.length > 1;
  const t = useTranslator();

  useEffect(() => {
    statReachGoal('live_create_view');
  }, []);

  useEffect(() => {
    if (liveStatus === LiveStatus.ONLINE) {
      setTimeout(() => setHideOnlineStatus(true), 2000);
    } else if (hideOnlineStatus) {
      setHideOnlineStatus(false);
    }
  }, [liveStatus, hideOnlineStatus]);

  const handleShare = useCallback(() => {
    const link: AttachLink = {
      type: 'live',
      object: user.domain || 'id' + user.id,
    };

    dispatch(
      mainActions.setPopup(
        <ShareSheet title={t('live_share_title')} link={link} disableInternalShare />,
      ),
    );

    statReachGoal('live_share', {
      liveId: stream?.id,
      streamerId: stream?.streamerId,
    });
  }, [stream, user]);

  const handleStopLive = () => {
    if (
      [
        LiveStatus.CONNECTING,
        LiveStatus.CREATING,
        LiveStatus.ONLINE,
        LiveStatus.RECONNECTION,
      ].includes(liveStatus)
    ) {
      dispatch(liveActions.setConfirmStop(true));
      return false;
    } else {
      dispatch(liveActions.stopStream());
      hideModal();
    }
    statReachGoal('live_stop');
    return true;
  };

  const handleMsgClick = useCallback(
    (msg: StreamMessageModel) => {
      if (user.id !== msg.fromId) {
        dispatch(liveActions.showActionsSheet(msg.fromId));
      }
    },
    [user],
  );

  useModalClose('live', () => {
    return handleStopLive();
  });

  const message = useMemo(() => {
    if (confirmStop) {
      return t('live_close_confirm_text');
    } else if (reconnecting && !togglingCamera) {
      return <>{t('live_reconnecting')}</>;
    } else if ([LiveStatus.CREATING, LiveStatus.CONNECTING].includes(liveStatus)) {
      return (
        <div className="LivePublisher__status">
          <Loader /> {t('live_create')}
        </div>
      );
    } else if (liveStatus === LiveStatus.ONLINE && !hideOnlineStatus) {
      return (
        <div className="LivePublisher__status ">
          <div className="LivePublisher__status__rec" />
          {t('live_online')}
        </div>
      );
    } else if (liveStatus === LiveStatus.ACCESS_CAM_ERROR) {
      return (
        <Placeholder
          iconSize={80}
          icon={<IcCamOff28 />}
          title={t('live_access_cam_title')}
        >
          {t('live_access_cam_desc')}
        </Placeholder>
      );
    } else if (liveStatus === LiveStatus.ACCESS_MIC_ERROR) {
      return (
        <Placeholder
          iconSize={80}
          icon={<IcMicOff28 />}
          title={t('live_access_mic_title')}
        >
          {t('live_access_mic_desc')}
        </Placeholder>
      );
    } else if (liveStatus === LiveStatus.ACCESS_CAM_AND_MIC_ERROR) {
      return (
        <Placeholder
          iconSize={80}
          icon={<IcMicOff28 />}
          title={t('live_access_cam_and_mic_title')}
        >
          {t('live_access_cam_and_mic_desc')}
        </Placeholder>
      );
    }
  }, [t, confirmStop, liveStatus, hideOnlineStatus, reconnecting, togglingCamera]);

  const hasError = useMemo(
    () =>
      [
        LiveStatus.ACCESS_MIC_ERROR,
        LiveStatus.ACCESS_CAM_ERROR,
        LiveStatus.ACCESS_CAM_AND_MIC_ERROR,
      ].includes(liveStatus),
    [liveStatus],
  );

  if (liveStatus === LiveStatus.FINISHED) {
    return (
      <div className="LivePublisher">
        <LiveEndStatistics />
      </div>
    );
  }

  return (
    <div className="LivePublisher">
      {!hasError && (
        <LiveHeader
          onTipsClick={() => dispatch(liveActions.setTotalTipsSheet(true))}
          onViewerClick={() => dispatch(liveActions.setOnlineUsersSheet(true))}
          showControls={!message && liveStatus === LiveStatus.ONLINE}
          tips={tipsTotal}
          createdAt={stream?.createdAt}
          user={user}
          viewers={online}
          onClose={!confirmStop ? handleStopLive : undefined}
        />
      )}
      <div className="LivePublisher__body">
        <LiveMediaStream
          muted={true}
          selfie={cameraType === CameraTypeEnum.USER}
          blur={confirmStop}
          solid={hasError}
          content={message}
        />
        {liveStatus === LiveStatus.READY && (
          <LiveTypes
            type={permission}
            onTypeChanged={(type) => {
              dispatch(liveActions.setPermission(type));
            }}
          />
        )}
        {liveStatus === LiveStatus.ONLINE && hideOnlineStatus && !message && (
          <>
            <div className="LivePublisher__controls">
              <div
                className="LivePublisher__controls__btn"
                onClick={() => {
                  dispatch(liveActions.toggleMic());
                  statReachGoal('live_toggle_mic', {
                    on: micEnabled,
                  });
                }}
              >
                {micEnabled ? <IcMic28 /> : <IcMicOff28 />}
              </div>
              <div className="LivePublisher__controls__btn" onClick={handleShare}>
                <IcShare24 />
              </div>
              {displayCamSwitch && (
                <div
                  className={cn('LivePublisher__controls__btn', {
                    disabled: reconnecting,
                  })}
                  onClick={() => {
                    dispatch(liveActions.toggleCamera());
                    statReachGoal('live_toggle_camera');
                  }}
                >
                  <IcSwitch28 />
                </div>
              )}
            </div>
            <LiveHistory onMsgClick={handleMsgClick} />
          </>
        )}
      </div>
      <BottomBar hasSafeArea={!focus}>
        {hasError ? (
          <Button onClick={() => hideModal()}>{t('close')}</Button>
        ) : confirmStop ? (
          <>
            <Button mode="destructive" onClick={() => dispatch(liveActions.stopLive())}>
              {t('live_end_button_confirm')}
            </Button>
            <Button
              mode="secondary"
              onClick={() => dispatch(liveActions.setConfirmStop(false))}
            >
              {t('cancel')}
            </Button>
          </>
        ) : liveStatus === LiveStatus.READY ? (
          <>
            <Button
              mode="destructive"
              onClick={() => {
                dispatch(liveActions.createLive());
                statReachGoal('live_create_button', {
                  permission,
                });
              }}
            >
              {t('live_create_button')}
            </Button>
            {displayCamSwitch && (
              <RoundButton
                onClick={() => dispatch(liveActions.toggleCamera())}
                size="normal"
                mode={'outline'}
                icon={<IcSwitch28 />}
              />
            )}
          </>
        ) : (
          <div className="LivePublisher__bottom">
            {liveStatus === LiveStatus.ONLINE && (
              <Tabs
                selected={historyFilter}
                onChange={(value) => {
                  dispatch(liveActions.setHistoryFilter(value as LiveHistoryFilterEnum));
                }}
                skipMargin
              >
                <TabsItem value={LiveHistoryFilterEnum.ALL}>
                  {t('live_history_tab_all')}
                </TabsItem>
                <TabsItem value={LiveHistoryFilterEnum.TIPS} counter={tipsMessagesCount}>
                  {t('live_history_tab_tips')}
                </TabsItem>
                <TabsItem value={LiveHistoryFilterEnum.SPONSORS}>
                  {t('live_history_tab_sponsors')}
                </TabsItem>
              </Tabs>
            )}
            <LivePrompt disabled={!(liveStatus === LiveStatus.ONLINE)} />
          </div>
        )}
      </BottomBar>
    </div>
  );
};
